// src/components/document-sets/DocumentSetSelector.js
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import DocumentSetPill from './DocumentSetPill';
import SelectionCheckbox from '../pdf-table/SelectionCheckbox';
import SetListItem from './SetListItem';
import PopoverWrapper from '../ui/PopoverWrapper';
import SearchInput from '../ui/SearchInput';
import ScrollContainer from '../ui/ScrollContainer';
import { getRandomColor } from '../../utils/documentSetColors';
import { trackDocumentAddedToSet, trackDocumentRemovedFromSet } from '../../utils/analytics';


const MAX_VISIBLE_DOTS = 9;

/**
 * Document Set Selector Component
 * 
 * FIXES APPLIED:
 * 1. Race condition fixed: Local state only initializes from props on mount
 * 2. Collapsed state: Shows up to X dots, then "+Y" for overflow
 * 3. Positioning: Popover appears to the right with start alignment
 */
const DocumentSetSelector = ({
  availableSets = [],
  selectedSetIds = [],
  onUpdateDocumentSets,
  onCreateSet,
  disabled = false,
  isBulkMode = false,
  onPopoverOpenChange,
  rowId,
  isCollapsed = false,
  forceRing = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef(null);

  // Use local state for immediate updates during user interaction
  const [localSelectedSetIds, setLocalSelectedSetIds] = useState(selectedSetIds);

  // Sync from props ONLY when popover is closed
  // This allows:
  // 1. External changes (optimistic updates, real-time listener) to flow through when popover is closed
  // 2. User's in-progress selections to be preserved while popover is open
  useEffect(() => {
    if (!isOpen) {
      // Only sync if the arrays are actually different
      const propsStr = JSON.stringify([...selectedSetIds].sort());
      const localStr = JSON.stringify([...localSelectedSetIds].sort());
      
      if (propsStr !== localStr) {
        setLocalSelectedSetIds(selectedSetIds);
      }
    }
  }, [selectedSetIds, isOpen]); // Note: localSelectedSetIds intentionally NOT in deps to avoid infinite loop

  // Get selected set objects for display
  const selectedSets = useMemo(() => {
    return localSelectedSetIds
      .map(id => availableSets.find(set => set.id === id))
      .filter(Boolean);
  }, [availableSets, localSelectedSetIds]);

  // Filter and sort available sets
  const filteredSets = useMemo(() => {
    let sets = availableSets;

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      sets = sets.filter(set =>
        set.name.toLowerCase().includes(query)
      );
    }

    return [...sets].sort((a, b) => {
      const aDate = a.lastModified?.toDate ? a.lastModified.toDate() : new Date(a.lastModified || 0);
      const bDate = b.lastModified?.toDate ? b.lastModified.toDate() : new Date(b.lastModified || 0);
      return bDate - aDate;
    });
  }, [availableSets, searchQuery]);

  // Check if search query could create a new set
  const canCreateNew = useMemo(() => {
    if (!searchQuery.trim()) return false;
    
    const exactMatch = availableSets.some(
      set => set.name.toLowerCase() === searchQuery.trim().toLowerCase()
    );
    
    return !exactMatch;
  }, [availableSets, searchQuery]);

  // Preview color for new set
  const [previewColor, setPreviewColor] = useState(null);

  useEffect(() => {
    if (searchQuery.trim() && !previewColor) {
      setPreviewColor(getRandomColor(availableSets));
    } else if (!searchQuery.trim() && previewColor) {
      setPreviewColor(null);
    }
  }, [searchQuery, previewColor, availableSets]);

  // Focus search input when popover opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      const input = searchInputRef.current.querySelector('input');
      if (input) input.focus();
    }
  }, [isOpen]);

  // Handle creating a new set
  const handleCreateSet = useCallback(async () => {
    if (!canCreateNew || !searchQuery.trim() || !previewColor) return;

    const newSetName = searchQuery.trim();
    const newSet = await onCreateSet?.(newSetName, previewColor);

    if (newSet?.id) {
      // Track adding document to newly created set
      trackDocumentAddedToSet(newSet.id, true);

      setLocalSelectedSetIds(currentSetIds => {
        const newSetIds = [...currentSetIds, newSet.id];
        onUpdateDocumentSets?.(newSetIds);
        return newSetIds;
      });
    }

    setSearchQuery('');
  }, [canCreateNew, searchQuery, previewColor, onCreateSet, onUpdateDocumentSets]);

  // Handle keyboard shortcuts
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && canCreateNew) {
      e.preventDefault();
      handleCreateSet();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  }, [canCreateNew, handleCreateSet]);

  // Handle individual set selection toggle
  const handleSetToggle = useCallback((set) => {
    if (set.archived) return;

    setLocalSelectedSetIds(currentSetIds => {
      const isSelected = currentSetIds.includes(set.id);
      const newSetIds = isSelected
        ? currentSetIds.filter(id => id !== set.id)
        : [...currentSetIds, set.id];

      // Track the action
      if (isSelected) {
        trackDocumentRemovedFromSet(set.id);
      } else {
        trackDocumentAddedToSet(set.id, false);
      }

      onUpdateDocumentSets?.(newSetIds);
      return newSetIds;
    });
  }, [onUpdateDocumentSets]);

  // Handle removing a set from pills
  const handleRemoveSet = useCallback((setId) => {
    const set = availableSets.find(s => s.id === setId);
    if (set?.archived) return;

    // Track removal
    trackDocumentRemovedFromSet(setId);

    setLocalSelectedSetIds(currentSetIds => {
      const newSetIds = currentSetIds.filter(id => id !== setId);
      onUpdateDocumentSets?.(newSetIds);
      return newSetIds;
    });
  }, [availableSets, onUpdateDocumentSets]);

  // Handle select/deselect all
  const handleToggleAll = useCallback(() => {
    const unarchived = filteredSets.filter(s => !s.archived);
    const unarchivedIds = unarchived.map(s => s.id);

    setLocalSelectedSetIds(currentSetIds => {
      const selectedUnarchivedCount = unarchivedIds.filter(id => currentSetIds.includes(id)).length;

      const newSetIds = selectedUnarchivedCount === unarchived.length
        ? currentSetIds.filter(id => !unarchivedIds.includes(id))
        : [...new Set([...currentSetIds, ...unarchivedIds])];

      onUpdateDocumentSets?.(newSetIds);
      return newSetIds;
    });
  }, [filteredSets, onUpdateDocumentSets]);

  // Calculate header checkbox state
  const headerCheckboxState = useMemo(() => {
    const unarchived = filteredSets.filter(s => !s.archived);
    if (unarchived.length === 0) return { checked: false, indeterminate: false };

    const selectedUnarchivedCount = unarchived.filter(s => localSelectedSetIds.includes(s.id)).length;
    const isAllSelected = selectedUnarchivedCount === unarchived.length;
    const isSomeSelected = selectedUnarchivedCount > 0 && !isAllSelected;

    return { checked: isAllSelected, indeterminate: isSomeSelected };
  }, [filteredSets, localSelectedSetIds]);

  // Handle popover state change
  const handleOpenChange = useCallback((newOpen) => {
    setIsOpen(newOpen);
    onPopoverOpenChange?.(newOpen);
  }, [onPopoverOpenChange]);

  // Trigger element
  const trigger = (
    <div
      className={`
        min-h-[32px] px-2 py-1 ring-0 rounded-app-sm cursor-pointer
        ${disabled ? 'opacity-50 cursor-not-allowed' : forceRing ? 'ring-1 ring-app-gray-400' : 'hover:ring-1 ring-app-gray-400'}
      `}
      data-guide-target={forceRing ? 'tag-guide-first-tags-cell' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) {
          setIsOpen(true);
          onPopoverOpenChange?.(true);
        }
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
      }}
    >
      {selectedSets.length === 0 ? (
        <div className="flex items-center gap-1 h-[24px] text-app-gray-500">
          <Plus className="w-3.5 h-3.5" />
          {availableSets.length === 0 && (
            <span className="text-app-xs">add tag</span>
          )}
        </div>
      ) : isCollapsed ? (
        /* Collapsed state: Show up to 10 dots, then "+X" */
        <div className="flex items-center gap-1 h-[24px]">
          {selectedSets.slice(0, MAX_VISIBLE_DOTS).map((set) => (
            <div
              key={set.id}
              className="w-2 h-2 rounded-full flex-shrink-0 border"
              style={{
                backgroundColor: set.color.border,
                borderColor: set.color.hover,
              }}
              title={set.name}
            />
          ))}
          {selectedSets.length > MAX_VISIBLE_DOTS && (
            <span className="text-app-2xs text-app-gray-500 font-medium flex-shrink-0 whitespace-nowrap ml-auto">
              +{selectedSets.length - MAX_VISIBLE_DOTS}
            </span>
          )}
        </div>
      ) : (
        /* Expanded state: Show full pills */
        <div className="flex flex-wrap gap-1">
          {selectedSets.map((set) => (
            <DocumentSetPill
              key={set.id}
              documentSet={set}
              onRemove={handleRemoveSet}
              showRemove={!set.archived}
              isDraggable={false}
              isReadOnly={false}
              isArchived={set.archived}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <PopoverWrapper
      trigger={trigger}
      open={isOpen}
      onOpenChange={handleOpenChange}
      align="start"
      side="right"
      sideOffset={16}
      theme="dark"
    >
      <div className="w-[280px] h-[256px] flex flex-col p-3">
        <div ref={searchInputRef} className="mb-2">
          <SearchInput
            searchTerm={searchQuery}
            onSearchChange={setSearchQuery}
            onKeyDown={handleKeyDown}
            placeholder="Search or create..."
            dark
          />
        </div>

        <ScrollContainer className="flex-1 min-h-0">
          {filteredSets.length === 0 && !canCreateNew ? (
            <div className="px-3 py-4 text-sm text-app-gray-400 text-center">
              No tags (yet). Enter a name to create one!
            </div>
          ) : (
            <>
              {filteredSets.length > 0 && (
                <div className="flex items-center gap-2 px-2 py-1 mb-1">
                  <SelectionCheckbox
                    checked={headerCheckboxState.checked}
                    indeterminate={headerCheckboxState.indeterminate}
                    onChange={handleToggleAll}
                    variant="check"
                    darkMode
                  />
                  <span className="text-app-xs text-app-gray-400 font-medium">
                    Select All
                  </span>
                </div>
              )}

              {filteredSets.map(set => {
                const isSelected = localSelectedSetIds.includes(set.id);
                return (
                  <SetListItem
                    key={set.id}
                    set={set}
                    isSelected={isSelected}
                    isArchived={set.archived}
                    onToggle={() => handleSetToggle(set)}
                    mode="check"
                    darkMode
                  />
                );
              })}

              {canCreateNew && previewColor && (
                <>
                  <div className="border-t border-app-gray-600 my-2" />
                  <button
                    onClick={handleCreateSet}
                    className="w-full flex items-center gap-2 px-2 py-1 hover:bg-app-gray-700 rounded"
                  >
                    <Plus className="w-4 h-4 text-app-accent flex-shrink-0" />
                    <DocumentSetPill
                      documentSet={{
                        id: 'preview',
                        name: searchQuery,
                        color: previewColor
                      }}
                      onRemove={() => {}}
                      showRemove={false}
                      isDraggable={false}
                      isReadOnly={true}
                    />
                  </button>
                </>
              )}
            </>
          )}
        </ScrollContainer>
      </div>
    </PopoverWrapper>
  );
};

export default DocumentSetSelector;